import styled from 'styled-components';
import { media } from './StyledCalendarBase.js';
import { COLORS } from '../../constants/colors.js';

const CARD_BORDER = '1px solid rgba(35, 170, 175, 0.15)';
const CARD_SHADOW = '0px 12px 30px rgba(15, 23, 42, 0.08)';
const COMPACT_VIEWPORT = '@media (max-width: 900px) and (min-width: 769px)';
const SHORT_VIEWPORT = '@media (max-height: 820px)';

export const AssistanceMainContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding: 20px 0 24px;
  background: #f6f9fb;
  box-sizing: border-box;
  height: calc(100vh - 150px);
  max-height: calc(100vh - 150px);
  min-height: 0;
  overflow: hidden;

  ${media.desktop} {
    padding: 18px 0 22px;
  }

  ${media.laptop} {
    padding: 16px 0 22px;
    height: calc(100vh - 138px);
    max-height: calc(100vh - 138px);
  }

  ${COMPACT_VIEWPORT} {
    padding: 14px 0 18px;
  }

  ${media.tablet} {
    padding: 14px 0 20px;
    height: calc(100vh - 132px);
    max-height: calc(100vh - 132px);
  }

  ${media.mobile} {
    padding: 12px 0 16px;
    height: auto;
    max-height: none;
    overflow-y: auto;
  }

  ${SHORT_VIEWPORT} {
    padding: 10px 0 4px;
    overflow-y: hidden;
  }
`;

export const AssistanceContent = styled.div`
  width: 100%;
  max-width: 1360px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 48px;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;

  ${media.laptop} {
    padding: 0 32px;
    gap: 12px;
  }

  ${COMPACT_VIEWPORT} {
    padding: 0 28px;
    gap: 12px;
  }

  ${media.tablet} {
    padding: 0 20px;
    gap: 10px;
  }

  ${media.mobile} {
    padding: 0 16px;
    gap: 12px;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px;
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  flex: 0 0 auto;

  ${media.laptop} {
    gap: 12px;
  }

  ${COMPACT_VIEWPORT} {
    gap: 10px;
  }

  ${media.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px;
  }
`;

export const MetricCard = styled.div`
  background: #ffffff;
  border: ${CARD_BORDER};
  border-radius: 14px;
  box-shadow: 0px 8px 18px rgba(15, 23, 42, 0.05);
  padding: 16px 18px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${media.laptop} {
    border-radius: 12px;
    padding: 14px 16px;
    gap: 5px;
  }

  ${media.tablet} {
    padding: 12px 14px;
    gap: 4px;
  }

  ${media.mobile} {
    padding: 12px 14px;
  }

  ${COMPACT_VIEWPORT} {
    padding: 14px 16px;
    gap: 5px;
  }

  ${SHORT_VIEWPORT} {
    padding: 10px 12px;
  }
`;

export const MetricLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6c7a89;
  letter-spacing: 0.04em;

  ${media.laptop} {
    font-size: 11px;
    letter-spacing: 0.03em;
  }

  ${media.tablet} {
    font-size: 10px;
    letter-spacing: 0.02em;
  }

  ${media.mobile} {
    font-size: 11px;
    letter-spacing: 0.02em;
  }

  ${COMPACT_VIEWPORT} {
    font-size: 11px;
  }

  ${SHORT_VIEWPORT} {
    font-size: 10px;
  }
`;

export const MetricValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #131c2b;

  ${media.laptop} {
    font-size: 22px;
  }

  ${media.tablet} {
    font-size: 20px;
  }

  ${media.mobile} {
    font-size: 20px;
  }

  ${COMPACT_VIEWPORT} {
    font-size: 22px;
  }

  ${SHORT_VIEWPORT} {
    font-size: 19px;
  }
`;

export const FiltersCard = styled.div`
  background: #ffffff;
  border: ${CARD_BORDER};
  border-radius: 14px;
  box-shadow: 0px 8px 18px rgba(15, 23, 42, 0.05);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 0 0 auto;

  ${media.laptop} {
    padding: 16px 18px;
    gap: 10px;
  }

  ${media.mobile} {
    padding: 16px;
    gap: 10px;
  }

  ${COMPACT_VIEWPORT} {
    padding: 16px 18px;
    gap: 10px;
  }

  ${SHORT_VIEWPORT} {
    padding: 12px 14px;
  }
`;

export const FiltersLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #131c2b;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;

  ${media.laptop} {
    flex-wrap: nowrap;
    gap: 10px 16px;
  }

  ${media.tablet} {
    flex-wrap: nowrap;
    gap: 8px 12px;
  }

  ${media.mobile} {
    flex-wrap: wrap;
    align-items: stretch;
  }

  ${COMPACT_VIEWPORT} {
    gap: 10px 14px;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px 12px;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${COMPACT_VIEWPORT} {
    gap: 6px;
  }

  ${SHORT_VIEWPORT} {
    gap: 4px;
  }

  ${media.laptop} {
    gap: 6px;
  }

  ${media.tablet} {
    gap: 4px;
  }
`;

export const FilterText = styled.span`
  font-size: 12px;
  color: #6c7a89;
  text-transform: lowercase;
`;

const baseInputStyles = `
  background: #ffffff;
  border: 1px solid rgba(19, 28, 43, 0.15);
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 14px;
  color: #131c2b;
  font-family: 'Montserrat', sans-serif;
  transition: border 0.2s ease, box-shadow 0.2s ease;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0px 0px 0px 3px rgba(35, 170, 175, 0.2);
  }
`;

export const DateInput = styled.input`
  ${baseInputStyles}
  min-width: 160px;

  ${media.laptop} {
    min-width: 140px;
    padding: 8px 12px;
  }

  ${media.tablet} {
    min-width: 130px;
  }

  ${media.mobile} {
    width: 100%;
    min-width: 120px;
  }

  ${COMPACT_VIEWPORT} {
    min-width: 130px;
    padding: 8px 12px;
  }

  ${SHORT_VIEWPORT} {
    min-width: 120px;
  }
`;

export const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${COMPACT_VIEWPORT} {
    gap: 6px;
  }

  ${SHORT_VIEWPORT} {
    gap: 5px;
  }

  ${media.laptop} {
    flex: 1;
    justify-content: flex-end;
    min-width: 0;
  }

  ${media.tablet} {
    justify-content: flex-end;
  }

  ${media.mobile} {
    width: 100%;
    justify-content: flex-start;
  }
`; 

export const SearchInput = styled.input`
  ${baseInputStyles}
  width: 240px;

  ${media.laptop} {
    width: 190px;
  }

  ${COMPACT_VIEWPORT} {
    width: 188px;
    padding: 8px 12px;
  }

  ${media.tablet} {
    width: 100%;
    max-width: 160px;
    padding: 8px 12px;
  }

  ${media.mobile} {
    width: 100%;
    max-width: none;
  }

  ${SHORT_VIEWPORT} {
    width: 180px;
  }
`;

export const IconButton = styled.button`
  border: none;
  background: rgba(35, 170, 175, 0.1);
  color: ${COLORS.PRIMARY};
  margin-left: 8px;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.2s ease;

  ${media.laptop} {
    margin-left: 6px;
    padding: 6px 10px;
  }

  ${media.tablet} {
    padding: 6px 10px;
  }

  &:hover {
    background: rgba(35, 170, 175, 0.18);
  }

  ${COMPACT_VIEWPORT} {
    margin-left: 4px;
    padding: 6px 10px;
  }

  ${SHORT_VIEWPORT} {
    padding: 6px 9px;
  }
`;

export const SearchIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

export const TableCard = styled.div`
  background: #ffffff;
  border: ${CARD_BORDER};
  border-radius: 18px;
  box-shadow: ${CARD_SHADOW};
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;

  ${COMPACT_VIEWPORT} {
    border-radius: 16px;
  }
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  ${COMPACT_VIEWPORT} {
    max-height: none;
  }

  ${SHORT_VIEWPORT} {
    max-height: clamp(260px, 52vh, 420px);
  }

  ${media.mobile} {
    max-height: clamp(220px, 58vh, 380px);
  }
`;

const columnsTemplate = '130px minmax(0, 1.2fr) minmax(0, 1fr) 130px 144px 82px 92px';
const laptopColumnsTemplate = '115px minmax(0, 1.1fr) minmax(0, 0.95fr) 115px 128px 72px 84px';
const tabletColumnsTemplate = '105px minmax(0, 0.95fr) minmax(0, 1.1fr) 105px 112px 64px 76px';
const mobileColumnsTemplate = '95px minmax(0, 0.9fr) minmax(0, 1.15fr) 95px 104px 58px 70px';

export const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${columnsTemplate};
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid rgba(19, 28, 43, 0.08);
  background: #f2f7fa;
  font-size: 13px;
  font-weight: 600;
  color: #556171;
  text-transform: uppercase;
  width: 100%;

  ${media.laptop} {
    grid-template-columns: ${laptopColumnsTemplate};
    gap: 14px;
    padding: 16px 20px;
    font-size: 12px;
  }

  ${COMPACT_VIEWPORT} {
    grid-template-columns: ${tabletColumnsTemplate};
    gap: 12px;
    padding: 14px 18px;
    font-size: 11px;
  }

  ${media.tablet} {
    grid-template-columns: ${tabletColumnsTemplate};
    gap: 12px;
    padding: 14px 18px;
    font-size: 11px;
  }

  ${media.mobile} {
    grid-template-columns: ${mobileColumnsTemplate};
    gap: 10px;
    padding: 12px 16px;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px;
    padding: 10px 14px;
  }
`;

export const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  width: 100%;
`;

export const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${columnsTemplate};
  gap: 16px;
  padding: 18px 24px;
  align-items: center;
  border-bottom: 1px solid rgba(19, 28, 43, 0.06);
  width: 100%;

  &:last-child {
    border-bottom: none;
  }

  ${media.laptop} {
    grid-template-columns: ${laptopColumnsTemplate};
    gap: 14px;
    padding: 16px 20px;
  }

  ${media.tablet} {
    grid-template-columns: ${tabletColumnsTemplate};
    gap: 12px;
    padding: 14px 18px;
  }

  ${media.mobile} {
    grid-template-columns: ${mobileColumnsTemplate};
    padding: 16px 20px;
  }

  ${COMPACT_VIEWPORT} {
    grid-template-columns: ${tabletColumnsTemplate};
    gap: 12px;
    padding: 14px 18px;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px;
    padding: 10px 14px;
  }
`;

export const TableCell = styled.div`
  font-size: 14px;
  color: #1f2a37;
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  min-width: 0;
  flex-wrap: wrap;
  row-gap: 4px;
  white-space: normal;

  ${media.laptop} {
    font-size: 13px;
  }

  ${media.tablet} {
    font-size: 12px;
  }

  ${COMPACT_VIEWPORT} {
    font-size: 12px;
  }

  ${SHORT_VIEWPORT} {
    font-size: 11px;
  }
`;

export const AttendanceSelect = styled.select`
  ${baseInputStyles}
  width: 100%;
  border: 1px solid rgba(108, 122, 137, 0.2);
  background: #ffffff;
  color: #1f2a37;

  option {
    color: #131c2b;
  }

  &:focus {
    background: #ffffff;
    color: #1f2a37;
    border-color: rgba(108, 122, 137, 0.35);
  }

  ${media.laptop} {
    padding: 8px 12px;
    font-size: 13px;
  }

  ${media.tablet} {
    padding: 6px 10px;
    font-size: 11px;
    border-radius: 10px;
  }

  ${media.mobile} {
    padding: 6px 10px;
    font-size: 11px;
  }

  ${COMPACT_VIEWPORT} {
    padding: 7px 10px;
    font-size: 12px;
    border-radius: 10px;
  }

  ${SHORT_VIEWPORT} {
    padding: 6px 9px;
    font-size: 11px;
  }

  &[data-attendance='default']:not(:focus) {
    background: rgba(108, 122, 137, 0.15);
    color: #4b5563;
    border-color: rgba(108, 122, 137, 0.2);
  }

  &[data-attendance='asistio']:not(:focus) {
    background: rgba(35, 170, 175, 0.18);
    color: #0b7174;
    border-color: rgba(35, 170, 175, 0.35);
  }

  &[data-attendance='no-asistio']:not(:focus) {
    background: rgba(239, 68, 68, 0.16);
    color: #b91c1c;
    border-color: rgba(239, 68, 68, 0.35);
  }

  &[data-attendance='ausente-con-aviso']:not(:focus) {
    background: rgba(245, 158, 11, 0.18);
    color: #92400e;
    border-color: rgba(245, 158, 11, 0.35);
  }

  &[data-attendance='pendiente']:not(:focus) {
    background: rgba(148, 163, 184, 0.18);
    color: #475569;
    border-color: rgba(148, 163, 184, 0.35);
  }
`;

export const StatusIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0;
`;

export const StatusIconImage = styled.img`
  width: 26px;
  height: 26px;
  object-fit: contain;

  ${media.laptop} {
    width: 22px;
    height: 22px;
  }

  ${media.tablet} {
    width: 20px;
    height: 20px;
  }

  ${COMPACT_VIEWPORT} {
    width: 22px;
    height: 22px;
  }

  ${SHORT_VIEWPORT} {
    width: 20px;
    height: 20px;
  }
`;

export const SummarySection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
  margin-top: auto;
  padding: 8px 0 0;

  ${media.laptop} {
    gap: 14px;
  }

  ${media.tablet} {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  ${COMPACT_VIEWPORT} {
    gap: 12px;
    padding: 6px 0 0;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px;
    padding: 2px 0 0;
  }
`;

export const TotalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  flex: 1;

  ${media.laptop} {
    gap: 12px;
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
  }

  ${COMPACT_VIEWPORT} {
    gap: 12px;
  }

  ${SHORT_VIEWPORT} {
    gap: 8px;
  }
`;

export const EmptyState = styled.div`
  padding: 28px;
  text-align: center;
  color: #6c7a89;
  font-size: 15px;
  width: 100%;

  ${media.laptop} {
    padding: 22px;
  }

  ${media.tablet} {
    padding: 18px;
  }
`;
